import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";
import { rm, credits, fmtDateDMY } from "../../lib/format";
import { openPaymentGateway } from "../../lib/payments";
import { paymentMethodLabel, primaryPayButtonLabel, sortPaymentMethods } from "../../lib/paymentMethods";
import { useAuth } from "../../stores/auth";
import { Button, Input } from "../ui";
import { toast } from "../../stores/toast";

interface FlexxiPlanPanelProps {
  planId: string;
  /** Hide the compact schedule (the dashboard renders its own). */
  hideSchedule?: boolean;
  /** Show a link to the full plan dashboard. */
  showDashboardLink?: boolean;
}

type PayMode = "MIN_DUE" | "FULL_REMAINING" | "CUSTOM";

type FlexxiPayResponse = {
  orderId: string;
  amount: number;
  payment: {
    paid?: boolean;
    status?: string;
    redirectUrl?: string;
    form?: { url: string; fields: Record<string, string> };
  };
};

export function FlexxiPlanPanel({ planId, hideSchedule, showDashboardLink }: FlexxiPlanPanelProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { config } = useAuth();
  const [method, setMethod] = useState("");
  const [customAmount, setCustomAmount] = useState("");

  const q = useQuery({
    queryKey: ["flexxi", "plan", planId],
    queryFn: () =>
      api<{
        plan: {
          statusLabel: string;
          paidTotal: number;
          totalPayable: number;
          remaining: number;
        };
        installments: { sequence: number; dueDate: string; amountDue: number; amountPaid: number; status: string }[];
        suggestedPayAmount: number;
      }>(`/flexxi/plans/${planId}`),
  });

  const walletQ = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<{ balance: number }>("/wallet"),
    enabled: Boolean(config?.creditsEnabled),
  });

  const methods = useMemo(() => {
    const pm = config?.paymentMethods;
    const list: string[] = [];
    if (pm?.bankIn) list.push("BANK_IN");
    if (config?.creditsEnabled) list.push("CREDITS");
    if (pm?.gkash) list.push("GKASH");
    if (pm?.stripe) list.push("STRIPE");
    return sortPaymentMethods(list);
  }, [config]);

  useEffect(() => {
    if (!method && methods.length) setMethod(methods[0]);
  }, [methods, method]);

  const payMut = useMutation({
    mutationFn: (body: { payMode: PayMode; customAmount?: number; method: string }) =>
      api<FlexxiPayResponse>(`/flexxi/plans/${planId}/pay`, { method: "POST", body }),
  });

  async function submitPay(payMode: PayMode, custom?: number) {
    if (!method) {
      toast("error", "Choose a payment method");
      return;
    }
    if (payMode === "CUSTOM") {
      const n = custom ?? Number(customAmount);
      if (!Number.isFinite(n) || n <= 0) {
        toast("error", "Enter a valid amount");
        return;
      }
    }
    try {
      const data = await payMut.mutateAsync({
        payMode,
        method,
        ...(payMode === "CUSTOM" ? { customAmount: custom ?? Number(customAmount) } : {}),
      });
      const payment = data.payment;
      if (payment.paid) {
        toast("success", "Payment applied");
        await qc.invalidateQueries({ queryKey: ["flexxi"] });
        await qc.invalidateQueries({ queryKey: ["vault"] });
        await qc.invalidateQueries({ queryKey: ["wallet"] });
        return;
      }
      if (payment.status === "REDIRECT" && payment.redirectUrl) {
        openPaymentGateway({ status: "REDIRECT", redirectUrl: payment.redirectUrl });
        return;
      }
      if (payment.status === "FORM" && payment.form) {
        openPaymentGateway({ status: "GKASH_FORM", form: payment.form });
        return;
      }
      navigate(`/checkout/order/${data.orderId}`);
    } catch (e) {
      toast("error", e instanceof ApiError ? e.message : "Payment failed");
    }
  }

  if (q.isLoading || !q.data) return <p className="text-sm text-muted">Loading plan…</p>;

  const { plan, installments, suggestedPayAmount } = q.data;
  const rate = config?.creditConversionRate ?? 1;
  const amountLabelFor = (myr: number) =>
    method === "CREDITS" ? credits(Math.ceil(myr * rate)) : rm(myr);

  return (
    <div className="space-y-3 rounded-2xl border border-line bg-hover/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">FlexxiPlan</span>
        <span className="text-xs text-brand-pink">{plan.statusLabel}</span>
      </div>
      <p className="text-sm">
        Paid {rm(plan.paidTotal)} of {rm(plan.totalPayable)} · {rm(plan.remaining)} remaining
      </p>
      {showDashboardLink ? (
        <Link to={`/account/flexxi/${planId}`} className="inline-block text-xs font-semibold text-brand-pink underline">
          View plan dashboard
        </Link>
      ) : null}
      {hideSchedule ? null : (
        <ul className="max-h-32 space-y-1 overflow-y-auto text-xs text-muted">
          {installments.map((i) => (
            <li key={i.sequence} className="flex justify-between gap-2">
              <span>
                #{i.sequence} · {fmtDateDMY(i.dueDate)}
              </span>
              <span>
                {rm(i.amountPaid)} / {rm(i.amountDue)} ({i.status})
              </span>
            </li>
          ))}
        </ul>
      )}

      {plan.remaining <= 0 ? null : methods.length === 0 ? (
        <p className="text-xs text-amber-300/90">No payment methods are enabled. Ask the store to turn on payments in settings.</p>
      ) : (
        <>
          <div>
            <p className="mb-1.5 text-[10px] uppercase tracking-wide text-muted">Payment method</p>
            <div className="flex flex-wrap gap-2">
              {methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`rounded-xl border px-3 py-2 text-left text-[11px] transition-colors ${
                    method === m ? "border-brand-pink bg-brand-pink/15 text-white" : "border-white/10 text-white/60"
                  }`}
                >
                  {paymentMethodLabel(m, {
                    walletBalance: walletQ.data?.balance,
                    creditConversionRate: rate,
                  })}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              disabled={payMut.isPending || !method || suggestedPayAmount <= 0}
              onClick={() => void submitPay("MIN_DUE")}
            >
              {method
                ? primaryPayButtonLabel(method, amountLabelFor(suggestedPayAmount))
                : "Choose payment method"}
            </Button>
            <Button
              variant="outline"
              disabled={payMut.isPending || !method || plan.remaining <= 0}
              onClick={() => void submitPay("FULL_REMAINING")}
            >
              {method === "BANK_IN"
                ? "Pay all remaining · bank transfer"
                : `Pay all remaining · ${amountLabelFor(plan.remaining)}`}
            </Button>
            <Input
              label="Custom amount (MYR)"
              inputMode="decimal"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
            />
            <Button
              variant="ghost"
              disabled={payMut.isPending || !method || !customAmount}
              onClick={() => void submitPay("CUSTOM")}
            >
              {method && customAmount
                ? primaryPayButtonLabel(method, amountLabelFor(Number(customAmount) || 0))
                : "Pay custom amount"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
